// BranchProductsForecast.jsx — compact forecast widget for branch staff.
//
// Two modes:
//   variant="daily"  → today's prep list (used in MyWorkPanel — barista)
//   variant="weekly" → 7-day product × day grid (used in BranchManager)
//
// Both pull from the same `/api/products/forecast/branch-*` endpoints,
// so updates to the forecast model on the Products page instantly
// reflect across the operation.
import React, { useEffect, useState } from "react";
import { api } from "./lib/api";
import { zVar } from "./lib/zIndex";

const fmt = (n) => Math.round(n || 0).toLocaleString("en-US");

export default function BranchProductsForecast({ variant = "daily", topN }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    const ep = variant === "weekly" ? "branch-weekly" : "branch-daily";
    const n = topN || (variant === "weekly" ? 12 : 15);
    (async () => {
      try {
        const { data } = await api.get(`/products/forecast/${ep}?top_n=${n}`);
        if (alive) setData(data);
      } catch (_e) { /* ignore */ }
      finally { if (alive) setLoading(false); }
    })();
    return () => { alive = false; };
  }, [variant, topN]);

  if (loading) {
    return <div style={{ padding: 14, textAlign: "center", color: "var(--muted)", fontSize: 12 }}>⏳ جارٍ تحميل التوقع...</div>;
  }
  if (!data?.ok) {
    return (
      <div style={{ padding: 14, background: "var(--beige)", borderRadius: 8, fontSize: 12, color: "var(--muted)", textAlign: "center" }}>
        ℹ {data?.message || "ارفع تقارير المنتجات لتفعيل التوقع."}
      </div>
    );
  }

  if (variant === "daily") {
    const items = data.items || [];
    return (
      <div data-testid="branch-forecast-daily">
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center",
          flexWrap: "wrap", gap: 8, marginBottom: 10, padding: "8px 12px",
          background: "#ECFEFF", borderRadius: 6, borderInlineStart: "4px solid #0891B2" }}>
          <div>
            <div style={{ fontSize: 13, fontWeight: 800, color: "#0E7490" }}>
              📅 توقع اليوم — {data.weekday} {data.date}
            </div>
            <div style={{ fontSize: 11, color: "var(--muted)" }}>
              جهّز هذه الكميات قبل بداية الشفت
            </div>
          </div>
          <div style={{ display: "flex", gap: 12, fontSize: 12, fontWeight: 700, color: "#0E7490" }}>
            <span>📦 {fmt(data.total_qty)} وحدة</span>
            <span>💰 {fmt(data.total_rev)} ر</span>
          </div>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))", gap: 8 }}>
          {items.map((it, i) => (
            <div key={i} data-testid={`bf-daily-${i}`} style={{
              padding: 10, background: "#fff", borderRadius: 6,
              border: "1px solid var(--border)", borderInlineStart: `4px solid ${
                it.confidence >= 75 ? "#16A34A" : it.confidence >= 50 ? "#D97706" : "#9CA3AF"
              }`,
            }}>
              <div style={{ fontSize: 12.5, fontWeight: 700, color: "var(--brown-deep)" }}>
                {it.product}
              </div>
              <div style={{ display: "flex", justifyContent: "space-between", marginTop: 4 }}>
                <span style={{ fontSize: 18, fontWeight: 800, color: "#0E7490" }}>{fmt(it.qty_today)}</span>
                <span style={{ fontSize: 11, color: "var(--muted)", alignSelf: "end" }}>وحدة متوقعة</span>
              </div>
              <div style={{ display: "flex", justifyContent: "space-between", marginTop: 2 }}>
                <span style={{ fontSize: 11, color: "var(--muted)" }}>{fmt(it.rev_today)} ر</span>
                <span style={{ fontSize: 10, color: it.confidence >= 75 ? "#16A34A" : "#92400E", fontWeight: 700 }}>
                  دقة {it.confidence}%
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  // weekly grid
  const rows = data.rows || [];
  const days = data.days_labels || [];
  return (
    <div data-testid="branch-forecast-weekly">
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center",
        flexWrap: "wrap", gap: 8, marginBottom: 10 }}>
        <div style={{ fontSize: 12, color: "var(--muted)" }}>
          توقع 7 أيام × أعلى {rows.length} منتج
        </div>
        <div style={{ fontSize: 12, fontWeight: 700, color: "#0E7490" }}>
          الإجمالي المتوقع: {fmt(data.grand_total)} وحدة
        </div>
      </div>
      <div className="tw" style={{ maxHeight: 380, overflowY: "auto" }}>
        <table>
          <thead>
            <tr>
              <th style={{ position: "sticky", insetInlineStart: 0, background: "var(--beige)", zIndex: zVar('base') }}>المنتج</th>
              {days.map((d, i) => (<th key={i} style={{ textAlign: "center", fontSize: 11 }}>{d}</th>))}
              <th style={{ textAlign: "center", background: "var(--beige)" }}>الإجمالي</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={i} data-testid={`bf-weekly-${i}`}>
                <td style={{ fontWeight: 700, fontSize: 11.5, position: "sticky", insetInlineStart: 0, background: "#fff" }}>
                  {r.product}
                </td>
                {(r.qtys || []).map((q, j) => (
                  <td key={j} style={{ textAlign: "center", fontSize: 11.5,
                    fontWeight: q === Math.max(...r.qtys) ? 800 : 500,
                    color: q === Math.max(...r.qtys) ? "#0E7490" : "var(--text)" }}>
                    {fmt(q)}
                  </td>
                ))}
                <td style={{ textAlign: "center", fontWeight: 800, color: "#0E7490", background: "#F0F9FF" }}>
                  {fmt(r.total)}
                </td>
              </tr>
            ))}
            <tr style={{ background: "var(--beige)", fontWeight: 800 }}>
              <td style={{ position: "sticky", insetInlineStart: 0, background: "var(--beige)" }}>الإجمالي</td>
              {(data.col_totals || []).map((c, j) => (
                <td key={j} style={{ textAlign: "center" }}>{fmt(c)}</td>
              ))}
              <td style={{ textAlign: "center", color: "#0E7490" }}>{fmt(data.grand_total)}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
}
